import React, {useState} from "react";
import { JsonView, allExpanded, darkStyles, defaultStyles } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';
import axios from 'axios';
import { API_CONFIG } from '../config';

interface EndpointCardProps {
    method: 'GET' | 'POST';
    path: string;
    description: string;
    parameters: { name: string, description: string }[];
    body: any | null;
    response: any | null;
    rateLimit?: null | {
        bucket: string;
        limit: number;
        timeSeconds: number;
    }
}

interface ParameterInput {
    [key: string]: string;
}

interface RateLimitInfo {
    remaining: number;
    limit: number;
    reset: number;
    bucket: string;
}

const EndpointCard: React.FC<EndpointCardProps> = (props: EndpointCardProps) => {
    const [collapsed, setCollapsed] = useState(true);
    const [showTester, setShowTester] = useState(false);
    const [parameterInputs, setParameterInputs] = useState<ParameterInput>({});
    const [requestBody, setRequestBody] = useState<string>('');
    const [testResponse, setTestResponse] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
    
    const getMethodColor = (method: string) => {
        switch (method) {
            case 'GET':
                return 'success';
            case 'POST':
                return 'primary';
            default:
                return 'secondary';
        }
    };

    const handleParameterChange = (paramName: string, value: string) => {
        setParameterInputs(prev => ({
            ...prev,
            [paramName]: value
        }));
    };

    const handleTestEndpoint = async () => {
        setIsLoading(true);
        setError(null);
        setTestResponse(null);
        setRateLimitInfo(null);

        try {
            let url = `${API_CONFIG.baseUrl}${props.path}`;
            // Replace parameters in URL
            Object.entries(parameterInputs).forEach(([key, value]) => {
                url = url.replace(`:${key}`, value);
            });

            let response;
            if (props.method === 'GET') {
                response = await axios.get(url, {
                    headers: API_CONFIG.defaultHeaders
                });
            } else {
                response = await axios.post(url, requestBody ? JSON.parse(requestBody) : undefined, {
                    headers: API_CONFIG.defaultHeaders
                });
            }

            setTestResponse(response.data);

            // Extract rate limit information from headers
            const rateLimitHeaders = {
                remaining: response.headers['x-ratelimit-remaining'],
                limit: response.headers['x-ratelimit-limit'],
                reset: response.headers['x-ratelimit-reset'],
                bucket: response.headers['x-ratelimit-bucket']
            };

            if (rateLimitHeaders.remaining !== undefined) {
                setRateLimitInfo({
                    remaining: parseInt(rateLimitHeaders.remaining),
                    limit: parseInt(rateLimitHeaders.limit),
                    reset: parseInt(rateLimitHeaders.reset),
                    bucket: rateLimitHeaders.bucket
                });
            }
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'An error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="card mt-3">
            <div className="card-header d-flex justify-content-between align-items-center" onClick={() => setCollapsed(!collapsed)}>
                <div className="d-flex align-items-center gap-2">
                    <span className={`badge bg-${getMethodColor(props.method)}`}>{props.method}</span>
                    <span className="text-bold">{props.path}</span>
                    <span className="small-description">{props.description}</span>
                </div>
                <button 
                    className={`btn btn-sm ${collapsed ? 'btn-outline-secondary' : 'btn-secondary'}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        setCollapsed(!collapsed);
                    }}
                >
                    {collapsed ? 'Expand' : 'Collapse'}
                </button>
            </div>
            <div className={collapsed ? 'card-body collapse' : 'card-body'}>
                <div className="mb-4">
                    <h5 className="text-muted mb-3">Rate Limit</h5>
                    {props.rateLimit ? (
                        <div className="p-3 bg-light rounded">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <span className="rate-limit-badge">Bucket: {props.rateLimit.bucket}</span>
                                <small className="text-muted" title="Requests for endpoints within the same bucket count for the same limit">
                                    <i className="bi bi-info-circle"></i>
                                </small>
                            </div>
                            <p className="mb-1">
                                <span className="text-bold">{props.rateLimit.limit}</span> requests per IP
                            </p>
                            <p className="mb-0">
                                Time window: <span className="text-bold">{props.rateLimit.timeSeconds}</span> seconds
                            </p>
                            <small className="text-muted mt-2 d-block">
                                Need a higher limit? <a href="mailto:nuno@facha.dev" className="text-primary">Contact me</a> with your use case.
                            </small>
                        </div>
                    ) : (
                        <div className="p-3 bg-light rounded">
                            <p className="text-muted mb-0">No rate limit applied</p>
                        </div>
                    )}
                </div>

                <div className="mb-4">
                    <h5 className="text-muted mb-3">URL Parameters</h5>
                    {props.parameters.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        <th>Parameter</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {props.parameters.map((parameter, index) => (
                                        <tr key={index}>
                                            <td><span className="endpoint-param">{parameter.name}</span></td>
                                            <td>{parameter.description}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="p-3 bg-light rounded">
                            <p className="text-muted mb-0">No parameters required</p>
                        </div>
                    )}
                </div>

                <div className="mb-4">
                    <h5 className="text-muted mb-3">Request Body</h5>
                    {props.body ? (
                        <div className="p-3 bg-light rounded">
                            <JsonView data={props.body} shouldExpandNode={allExpanded} style={defaultStyles}/>
                        </div>
                    ) : (
                        <div className="p-3 bg-light rounded">
                            <p className="text-muted mb-0">No request body required</p>
                        </div>
                    )}
                </div>

                <div>
                    <h5 className="text-muted mb-3">Response</h5>
                    {props.response ? (
                        <div className="p-3 bg-light rounded">
                            <JsonView data={props.response} shouldExpandNode={allExpanded} style={defaultStyles}/>
                        </div>
                    ) : (
                        <div className="p-3 bg-light rounded">
                            <p className="text-muted mb-0">Empty response</p>
                        </div>
                    )}
                </div>

                <div className="mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="text-muted mb-0">Test Endpoint</h5>
                        <button 
                            className="btn btn-link text-decoration-none"
                            onClick={() => setShowTester(!showTester)}
                        >
                            {showTester ? 'Hide Tester' : 'Show Tester'}
                        </button>
                    </div>

                    {showTester && (
                        <div className="tester-section">
                            {props.parameters.length > 0 && (
                                <div className="mb-4">
                                    <div className="parameters-list">
                                        {props.parameters.map((param, index) => (
                                            <div key={index} className="parameter-item">
                                                <div className="parameter-header">
                                                    <span className="parameter-name">{param.name}</span>
                                                    <span className="parameter-type">string</span>
                                                </div>
                                                <div className="parameter-description">
                                                    {param.description}
                                                </div>
                                                <div className="parameter-input">
                                                    <div className="form-floating">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            id={`param-${param.name}`}
                                                            placeholder={`Enter ${param.name}`}
                                                            value={parameterInputs[param.name] || ''}
                                                            onChange={(e) => handleParameterChange(param.name, e.target.value)}
                                                        />
                                                        <label htmlFor={`param-${param.name}`}>Value</label>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {props.method === 'POST' && props.body && (
                                <div className="mb-4">
                                    <div className="form-floating">
                                        <textarea
                                            className="form-control"
                                            id="request-body"
                                            placeholder="Enter JSON request body"
                                            value={requestBody}
                                            onChange={(e) => setRequestBody(e.target.value)}
                                            style={{ height: '150px' }}
                                        />
                                        <label htmlFor="request-body">Request Body (JSON)</label>
                                    </div>
                                </div>
                            )}

                            <div className="d-flex justify-content-end">
                                <button
                                    className="btn btn-primary px-4"
                                    onClick={handleTestEndpoint}
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Testing...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-play-circle me-2"></i>
                                            Test Endpoint
                                        </>
                                    )}
                                </button>
                            </div>

                            {error && (
                                <div className="alert alert-danger mt-3 d-flex align-items-center">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    {error}
                                </div>
                            )}

                            {testResponse && (
                                <div className="mt-4">
                                    {rateLimitInfo && (
                                        <div className="rate-limit-info mb-4">
                                            <h6 className="text-muted mb-3">Rate Limit Status</h6>
                                            <div className="d-flex flex-wrap gap-3">
                                                <div className="rate-limit-stat">
                                                    <span className="stat-label">Remaining</span>
                                                    <span className={`stat-value ${rateLimitInfo.remaining < 10 ? 'text-warning' : 'text-success'}`}>
                                                        {rateLimitInfo.remaining}
                                                    </span>
                                                </div>
                                                <div className="rate-limit-stat">
                                                    <span className="stat-label">Limit</span>
                                                    <span className="stat-value">{rateLimitInfo.limit}</span>
                                                </div>
                                                <div className="rate-limit-stat">
                                                    <span className="stat-label">Reset In</span>
                                                    <span className="stat-value">
                                                        {Math.ceil((rateLimitInfo.reset - Date.now() / 1000) / 60)} min
                                                    </span>
                                                </div>
                                                <div className="rate-limit-stat">
                                                    <span className="stat-label">Bucket</span>
                                                    <span className="stat-value">{rateLimitInfo.bucket}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <h6 className="text-muted mb-3">Response</h6>
                                    <div className="p-3 bg-light rounded">
                                        <JsonView data={testResponse} shouldExpandNode={allExpanded} style={defaultStyles}/>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default EndpointCard;
